/**
 * FleetManager Max - Document Compliance Rules Configuration
 *
 * This file defines the document requirements for each scope (VEHICLE, DRIVER, etc.)
 * Used by DocumentComplianceEngine to evaluate document compliance.
 *
 * Rule Properties:
 * - required: boolean - Is the document mandatory?
 * - renewable: boolean - Does this document expire and need renewal?
 * - renewalWarningDays: number - Days before expiry to show warning
 * - minDocuments: number - Minimum documents required in folder
 * - conditionalOn: object - Condition for when this rule applies
 *   - field: string - Entity field to check
 *   - operator: 'in' | 'not_in' | 'equals' | 'not_equals' | 'exists' | 'not_exists'
 *   - values: any - Value(s) to compare against
 * - requiresValidation: boolean - Does this document need human validation?
 */

import { FOLDER_STRUCTURE } from './docstore.js';
import { convertFolderStructureToRules } from '../lib/DocumentComplianceEngine.js';

/**
 * FleetManager-specific requirement overrides
 * Format: 'SCOPE.FOLDER_CODE': { required, renewable, conditionalOn, ... }
 */
export const FLEETMANAGER_REQUIREMENTS = {
  // =============================================================================
  // VEHICLE Requirements
  // =============================================================================

  // Registration is ALWAYS required for any vehicle
  'VEHICLE.10_REGISTRATION': {
    required: true,
    renewable: false, // Registration doesn't expire (carte grise)
    minDocuments: 1,
    requiresValidation: true // Human must verify VIN, plate, owner match
  },

  // Technical inspection required and renewable (contrôle technique)
  'VEHICLE.20_TECHNICAL_INSPECTION': {
    required: true,
    renewable: true,
    renewalWarningDays: 60, // 2 months warning
    minDocuments: 1,
    requiresValidation: true // Human must verify dates and result
  },

  // Insurance is ALWAYS required and renewable
  'VEHICLE.30_INSURANCE': {
    required: true,
    renewable: true,
    renewalWarningDays: 30, // 1 month warning
    minDocuments: 1, // At least carte verte
    requiresValidation: true // Human must verify policy and dates
  },

  // Leasing/Rental only required if vehicle is leased or rented
  'VEHICLE.40_LEASING_RENTAL': {
    required: true,
    renewable: false,
    minDocuments: 1,
    requiresValidation: false, // Contract docs - no validation needed
    conditionalOn: {
      field: 'acquisition_type',
      operator: 'in',
      values: ['lease', 'lld', 'loa', 'rental', 'location']
    }
  },

  // Maintenance invoices - optional but tracked
  'VEHICLE.50_MAINTENANCE_INVOICES': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Claims - optional
  'VEHICLE.60_CLAIMS': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Misc - optional
  'VEHICLE.90_MISC': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // =============================================================================
  // DRIVER Requirements
  // =============================================================================

  // Driving licence is ALWAYS required and renewable
  'DRIVER.10_DRIVING_LICENCE': {
    required: true,
    renewable: true,
    renewalWarningDays: 90, // 3 months warning (important!)
    minDocuments: 1,
    requiresValidation: true // Human must verify license number, expiry, categories
  },

  // ID Documents - optional (may be needed for some organizations)
  'DRIVER.20_ID_DOCUMENTS': {
    required: false,
    renewable: true,
    renewalWarningDays: 90,
    requiresValidation: false
  },

  // Certifications - conditional on driver type
  'DRIVER.30_CERTIFICATIONS': {
    required: true,
    renewable: true,
    renewalWarningDays: 60,
    minDocuments: 1,
    requiresValidation: true, // Human must verify certification validity
    conditionalOn: {
      field: 'requires_certification',
      operator: 'equals',
      values: true
    }
  },

  // Sanctions - optional (tracked when they occur)
  'DRIVER.40_SANCTIONS': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // =============================================================================
  // CLAIM Requirements
  // =============================================================================

  // Declaration is required for any claim
  'CLAIM.10_DECLARATION': {
    required: true,
    renewable: false,
    minDocuments: 1, // At least the accident report
    requiresValidation: false // Claim docs don't need human validation
  },

  // Expert reports - conditional on claim type
  'CLAIM.20_EXPERT_REPORTS': {
    required: true,
    renewable: false,
    minDocuments: 1,
    requiresValidation: false,
    conditionalOn: {
      field: 'requires_expert',
      operator: 'equals',
      values: true
    }
  },

  // Invoices - optional but important for repair claims
  'CLAIM.30_INVOICES': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Correspondence - optional
  'CLAIM.40_CORRESPONDENCE': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Settlements - required for closed claims
  'CLAIM.50_SETTLEMENTS': {
    required: true,
    renewable: false,
    minDocuments: 1,
    requiresValidation: false,
    conditionalOn: {
      field: 'status',
      operator: 'in',
      values: ['settled', 'closed']
    }
  },

  // =============================================================================
  // CONTRACT Requirements
  // =============================================================================

  // Special conditions always required
  'CONTRACT.10_SPECIAL_CONDITIONS': {
    required: true,
    renewable: false,
    minDocuments: 1,
    requiresValidation: false
  },

  // General conditions - required
  'CONTRACT.20_GENERAL_CONDITIONS': {
    required: true,
    renewable: false,
    minDocuments: 1,
    requiresValidation: false
  },

  // Amendments - optional
  'CONTRACT.30_AMENDMENTS': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Invoices/Receipts - optional
  'CONTRACT.40_INVOICES_RECEIPTS': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // Attestations/Certificates - optional
  'CONTRACT.50_ATTESTATIONS': {
    required: false,
    renewable: false,
    requiresValidation: false
  },

  // =============================================================================
  // ADMIN Requirements
  // =============================================================================

  'ADMIN.ADMIN': {
    required: false,
    renewable: false,
    requiresValidation: false
  }
};

/**
 * Generate rules array from FOLDER_STRUCTURE + requirements
 */
export const COMPLIANCE_RULES = convertFolderStructureToRules(
  FOLDER_STRUCTURE,
  FLEETMANAGER_REQUIREMENTS
);

/**
 * Status label overrides for FleetManager (French)
 */
export const STATUS_LABELS = {
  complete: { label: 'Complet', icon: 'CheckCircle', color: 'green' },
  missing: { label: 'Requis manquant', icon: 'AlertTriangle', color: 'red' },
  expiring: { label: 'Expire bientôt', icon: 'Clock', color: 'orange' },
  expired: { label: 'Expiré', icon: 'XCircle', color: 'red' },
  empty: { label: 'Pas de document', icon: 'Minus', color: 'gray' },
  has_docs: { label: 'document(s)', icon: 'FileText', color: 'blue' },
  not_applicable: { label: 'Non applicable', icon: 'Slash', color: 'gray' },
  // NEW - Validation workflow states
  pending_validation: { label: 'En attente de validation', icon: 'Clock', color: 'amber' },
  validation_rejected: { label: 'Rejeté', icon: 'XCircle', color: 'red' },
  exception_recorded: { label: 'Exception enregistrée', icon: 'AlertTriangle', color: 'yellow' }
};

/**
 * Validation fields per document type
 * Defines which fields should be verified during human validation
 */
export const VALIDATION_FIELDS = {
  // Vehicle Registration (Carte Grise)
  VEHICLE_REGISTRATION: [
    { code: 'license_plate', label: 'Immatriculation', type: 'text', entityField: 'license_plate' },
    { code: 'vin', label: 'Numéro VIN', type: 'text', entityField: 'vin' },
    { code: 'owner_name', label: 'Nom propriétaire', type: 'text' },
    { code: 'first_registration_date', label: 'Date 1ère mise en circulation', type: 'date', entityField: 'first_registration_date' },
    { code: 'make', label: 'Marque', type: 'text', entityField: 'make' },
    { code: 'model', label: 'Modèle', type: 'text', entityField: 'model' }
  ],

  // Technical Inspection (Contrôle Technique)
  TECHNICAL_INSPECTION: [
    { code: 'inspection_date', label: 'Date du contrôle', type: 'date' },
    { code: 'next_inspection_date', label: 'Prochain contrôle', type: 'date' },
    { code: 'result', label: 'Résultat', type: 'select', options: ['Favorable', 'Défavorable', 'Contre-visite'] },
    { code: 'mileage', label: 'Kilométrage', type: 'number' },
    { code: 'center_name', label: 'Centre de contrôle', type: 'text' }
  ],

  // Insurance Card (Carte Verte / Attestation)
  INSURANCE_CARD: [
    { code: 'policy_number', label: 'N° Police', type: 'text' },
    { code: 'insurer', label: 'Assureur', type: 'text' },
    { code: 'valid_from', label: 'Valide du', type: 'date' },
    { code: 'valid_to', label: 'Valide au', type: 'date' },
    { code: 'vehicle_covered', label: 'Véhicule assuré', type: 'text', entityField: 'license_plate' }
  ],

  // Driving Licence (Permis de Conduire)
  DRIVING_LICENCE: [
    { code: 'license_number', label: 'Numéro de permis', type: 'text', entityField: 'license_number' },
    { code: 'holder_name', label: 'Nom du titulaire', type: 'text', entityField: '_holder_name' },
    { code: 'date_of_birth', label: 'Date de naissance', type: 'date', entityField: 'birth_date' },
    { code: 'categories', label: 'Catégories', type: 'text', entityField: '_categories_display' },
    { code: 'expiry_date', label: 'Date d\'expiration', type: 'date', entityField: 'license_expiry' },
    { code: 'obtained_date', label: 'Date d\'obtention', type: 'date', entityField: 'license_issue_date' },
    { code: 'issue_date', label: 'Date de délivrance', type: 'date' }
  ],

  // Driver Certification (FIMO, FCO, ADR, etc.)
  DRIVER_CERTIFICATION: [
    { code: 'certification_type', label: 'Type de certification', type: 'select', options: ['FIMO', 'FCO', 'ADR', 'CACES', 'Autre'] },
    { code: 'certification_number', label: 'Numéro', type: 'text' },
    { code: 'holder_name', label: 'Titulaire', type: 'text' },
    { code: 'valid_from', label: 'Valide du', type: 'date' },
    { code: 'valid_to', label: 'Valide au', type: 'date' },
    { code: 'issuer', label: 'Organisme émetteur', type: 'text' }
  ]
};

/**
 * Rejection reasons for document validation
 */
export const REJECTION_REASONS = [
  { code: 'wrong_type', label: 'Mauvais type de document' },
  { code: 'illegible', label: 'Document illisible / mauvaise qualité' },
  { code: 'expired', label: 'Document expiré' },
  { code: 'wrong_entity', label: 'Document ne correspond pas à l\'entité' },
  { code: 'incomplete', label: 'Document incomplet' },
  { code: 'other', label: 'Autre (préciser)' }
];

/**
 * Cannot provide reasons for document exception
 */
export const CANNOT_PROVIDE_REASONS = [
  { code: 'lost', label: 'Document perdu / détruit' },
  { code: 'admin_delay', label: 'Délai administratif (remplacement en cours)' },
  { code: 'legal', label: 'Restriction légale / confidentialité' },
  { code: 'other', label: 'Autre circonstance exceptionnelle' }
];

/**
 * Warning thresholds for FleetManager (days)
 */
export const WARNING_THRESHOLDS = {
  critical: 30,   // Red - expires within 30 days
  warning: 60,    // Orange - expires within 60 days
  notice: 90      // Yellow - expires within 90 days
};

/**
 * Get pre-configured engine config for FleetManager
 */
export function getFleetManagerConfig() {
  return {
    rules: COMPLIANCE_RULES,
    statusLabels: STATUS_LABELS,
    warningThresholds: WARNING_THRESHOLDS,
    getDocumentExpiry: (doc) => doc.valid_to || doc.expiry_date || doc.date_fin
  };
}

const complianceConfig = {
  COMPLIANCE_RULES,
  FLEETMANAGER_REQUIREMENTS,
  STATUS_LABELS,
  WARNING_THRESHOLDS,
  getFleetManagerConfig
};

export default complianceConfig;
